import Dexie from 'dexie';

class ChatDatabase extends Dexie {
  constructor() {
    super('ChatDatabase');
    this.version(2).stores({
      messages: '++id, session_uuid, message_index, role, content, timestamp, response_data'
    });
  }
}

const db = new ChatDatabase();

// Helper function to sanitize data for IndexedDB storage
const sanitizeForStorage = (data) => {
  if (data === null || data === undefined) {
    return data;
  }
  
  // Handle circular references and non-serializable objects
  try {
    return JSON.parse(JSON.stringify(data, (key, value) => {
      // Skip circular references and non-serializable objects
      if (value instanceof Window || value instanceof Element || 
          value instanceof Error || typeof value === 'function') {
        return '[Object]';
      }
      return value;
    }));
  } catch (error) {
    // If still fails, return basic object with error info
    if (typeof data === 'object' && data.message) {
      return { error: data.message, type: 'sanitized_error' };
    }
    return null;
  }
};

export const chatService = {
  async addMessage(role, content, sessionUuid, responseData = null) {
    try {
      // Get the current highest message_index for this session
      const existingMessages = await db.messages
        .where('session_uuid')
        .equals(sessionUuid)
        .toArray();
      
      // Sort by message_index to get the correct next index
      existingMessages.sort((a, b) => a.message_index - b.message_index);
      const nextIndex = existingMessages.length; // 0, 1, 2, 3...
      
      // Sanitize data to ensure it can be stored in IndexedDB
      const sanitizedContent = sanitizeForStorage(content);
      const sanitizedResponseData = sanitizeForStorage(responseData);
      
      const messageData = {
        session_uuid: sessionUuid,
        message_index: nextIndex,
        role: role, // 'user' or 'assistant'
        content: sanitizedContent,
        timestamp: new Date().toISOString(),
        response_data: sanitizedResponseData // Store complete API response for bot messages
      };
      
      await db.messages.add(messageData);
      return messageData;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  },

  async getMessages(sessionUuid) {
    const messages = await db.messages
      .where('session_uuid')
      .equals(sessionUuid)
      .toArray();
    
    // Sort by message_index to ensure proper order
    return messages.sort((a, b) => a.message_index - b.message_index);
  },

  async clearMessages(sessionUuid) {
    return await db.messages
      .where('session_uuid')
      .equals(sessionUuid)
      .delete();
  },

  async clearAllMessages() {
    // Clear entire IndexedDB and reset auto-increment
    await db.messages.clear();
    
    // Force reset auto-increment by recreating the database
    await db.delete();
    await db.open();
    
    return true;
  },

  async getAllSessions() {
    const messages = await db.messages.orderBy('timestamp').toArray();
    const sessions = {};
    
    messages.forEach(msg => {
      if (!sessions[msg.session_uuid]) {
        sessions[msg.session_uuid] = {
          uuid: msg.session_uuid,
          lastMessage: msg.timestamp,
          messageCount: 0
        };
      }
      sessions[msg.session_uuid].messageCount++;
      if (msg.timestamp > sessions[msg.session_uuid].lastMessage) {
        sessions[msg.session_uuid].lastMessage = msg.timestamp;
      }
    });
    
    return Object.values(sessions).sort((a, b) => 
      new Date(b.lastMessage) - new Date(a.lastMessage)
    );
  }
};

export default db;